import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../lib/supabase';
import { Lead } from '../types';
import { acmProductName, countAcmDoors } from '../lib/acmDoor';

export const LabelPrint: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [lead, setLead] = useState<Lead | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLead = async () => {
            if (!id || !supabase) return;
            try {
                const { data, error } = await supabase
                    .from('leads')
                    .select('*')
                    .eq('id', id)
                    .single();

                if (error) throw error;
                setLead(data);
            } catch (err) {
                console.error('Erro ao buscar lead para etiqueta:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchLead();
    }, [id]);

    useEffect(() => {
        if (lead && !loading) {
            // Pequeno atraso para garantir que a imagem do QR Code seja renderizada antes de abrir a janela de impressão
            setTimeout(() => {
                window.print();
            }, 500);
        }
    }, [lead, loading]);

    if (loading) {
        return <div className="p-10 text-center">Carregando dados da etiqueta...</div>;
    }

    if (!lead) {
        return <div className="p-10 text-center text-red-700">Lead não encontrado.</div>;
    }

    const qrUrl = `${window.location.origin}/leads?open=${lead.id}`;
    
    // Extraindo dados ACM, se houver
    const metadata = lead.metadata || {};
    const doors = Array.isArray(metadata.acm_doors) ? metadata.acm_doors : [];
    const quantidade = countAcmDoors(metadata) || 1;
    const tipo = acmProductName(metadata) || 'Porta Padrão';
    const coresPortas = Array.from(new Set(doors.map((d: any) => d?.cor_a).filter(Boolean))).join(', ');
    const cor = coresPortas || metadata.detalhes_acabamento || 'Não especificada';

    return (
        <div className="bg-white min-h-screen text-black font-sans">
            {/* O tamanho 10x15cm é aprox 378x567 pixels a 96 DPI, mas configuramos via CSS media query se necessário. 
                Neste layout manteremos tudo flexível para caber na folha térmica */}
            <div className="w-[378px] mx-auto p-4 border border-dashed border-gray-300 print:border-none print:w-full print:max-w-[10cm] print:m-0 print:p-2">
                <div className="text-center mb-4 border-b-2 border-black pb-2">
                    <h1 className="text-2xl font-black uppercase tracking-tight leading-none mb-1">
                        ORDEM DE PRODUÇÃO
                    </h1>
                    <p className="text-sm font-bold">ID: #{lead.id}</p>
                </div>

                <div className="space-y-3 mb-6">
                    <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">Cliente</p>
                        <p className="text-lg font-bold leading-tight">{lead.name}</p>
                    </div>

                    <div>
                        <p className="text-xs text-gray-500 uppercase font-bold">Tipo / Modelo</p>
                        <p className="text-base font-bold leading-tight uppercase">{tipo}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Quantidade</p>
                            <p className="text-xl font-black">{quantidade} UN</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase font-bold">Cor/Acabamento</p>
                            <p className="text-base font-bold leading-tight uppercase">{cor}</p>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-center pt-4 border-t-2 border-black">
                    <QRCodeSVG 
                        value={qrUrl} 
                        size={140}
                        level="H"
                        includeMargin={true}
                    />
                    <p className="text-[11px] text-center mt-2 font-bold uppercase">
                        Escaneie para abrir
                    </p>
                </div>
                
                {/* Linha de corte/botão para evitar que a janela de impressão trave o usuário que quiser voltar */}
                <div className="mt-8 text-center print:hidden">
                    <button 
                        onClick={() => window.close()} 
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-bold text-sm"
                    >
                        Fechar Aba
                    </button>
                    <button 
                        onClick={() => window.print()} 
                        className="px-4 py-2 bg-black text-white rounded-lg font-bold text-sm ml-2"
                    >
                        Imprimir Novamente
                    </button>
                </div>
            </div>
            
            {/* CSS para forçar configurações de impressão térmica */}
            <style dangerouslySetInnerHTML={{__html: `
                @media print {
                    @page { margin: 0; size: 10cm 15cm; }
                    body { background: white; -webkit-print-color-adjust: exact; }
                }
            `}} />
        </div>
    );
};
